import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import type { RootState } from "redux/types";
import { LayoutSplashScreen } from "../../../../../_metronic/layout/_core/MetronicSplashScreen";
import Button from "../../../../components/flat/ui/Button";
import AuthPasswordLayout, {
  AUTH_ILLUSTRATIONS,
  ButtonText,
  Container,
  Div,
  PanelIllustration,
  Subtitle,
  Title,
} from "../../components/AuthPasswordLayout";
import { logoutAuthService, refreshAuthServiceSession } from "../../_redux/authCrud";
import * as auth from "../../_redux/authRedux";
import {
  beginCallbackSessionAttempt,
  cancelPendingCallbackSession,
  isPendingCallbackSessionCancelled,
} from "../../_redux/pendingCallbackSession";
import {
  BACK_TO_LOGIN_LABEL,
  CALLBACK_FAILURE_MESSAGE,
  CALLBACK_FAILURE_TITLE,
  CallbackFailure,
  CallbackOutcomeKind,
  LOGIN_ROUTE,
  SessionPhase,
  type CallbackFailureValue,
  type SessionPhaseValue,
} from "./constants";
import { parseSessionTokens, readCallbackOutcome } from "./helpers";

type AuthServiceSession = {
  idToken: string;
  expiresIn?: number;
};

type CallbackProps = {
  hasAuthToken: boolean;
  login: (authToken: string, session: AuthServiceSession) => void;
  logout: () => void;
};

type CallbackFailureViewProps = {
  failure: CallbackFailureValue;
};

async function endFailedSession(logout: CallbackProps["logout"]): Promise<void> {
  try {
    await logoutAuthService();
  } catch (error) {
    console.error("[auth] callback: logout unavailable", {
      status: error?.response?.status,
    });
  }
  logout();
}

async function establishSession(
  login: CallbackProps["login"],
  logout: CallbackProps["logout"],
): Promise<SessionPhaseValue> {
  try {
    const tokens = parseSessionTokens(await refreshAuthServiceSession());

    if (!tokens) {
      console.error("[auth] callback: invalid session response");
      await endFailedSession(logout);
      return SessionPhase.FAILED;
    }

    if (isPendingCallbackSessionCancelled()) {
      return SessionPhase.FAILED;
    }

    login(tokens.accessToken, {
      idToken: tokens.idToken,
      expiresIn: tokens.expiresIn,
    });
    return SessionPhase.AWAITING_USER;
  } catch (error) {
    console.error("[auth] callback: session unavailable", {
      status: error?.response?.status,
    });
    await endFailedSession(logout);
    return SessionPhase.FAILED;
  }
}

function CallbackFailureView({ failure }: CallbackFailureViewProps) {
  const history = useHistory();

  return (
    <AuthPasswordLayout
      rightContent={
        <PanelIllustration src={AUTH_ILLUSTRATIONS.HOME_LOGIN} alt="" />
      }
    >
      <Container>
        <Div>
          <Title>{CALLBACK_FAILURE_TITLE}</Title>
          <Subtitle>{CALLBACK_FAILURE_MESSAGE[failure]}</Subtitle>
        </Div>
        <Button
          width="100%"
          padding="10px 40px"
          borderRadius="5px"
          hoverBackgroundColor="#007656"
          onClick={() => history.push(LOGIN_ROUTE)}
        >
          <ButtonText>{BACK_TO_LOGIN_LABEL}</ButtonText>
        </Button>
      </Container>
    </AuthPasswordLayout>
  );
}

function Callback({ hasAuthToken, login, logout }: CallbackProps) {
  const { search } = useLocation();
  const outcome = readCallbackOutcome(search);
  const isOkOutcome = outcome.kind === CallbackOutcomeKind.OK;
  const isErrorOutcome = outcome.kind === CallbackOutcomeKind.ERROR;
  const isInvalidOutcome = outcome.kind === CallbackOutcomeKind.INVALID;
  const sessionRequestRef = useRef<Promise<SessionPhaseValue> | null>(null);
  const [sessionPhase, setSessionPhase] = useState<SessionPhaseValue>(
    SessionPhase.STARTING,
  );

  useEffect(() => {
    if (isInvalidOutcome) {
      cancelPendingCallbackSession();
      logout();
    }
  }, [isInvalidOutcome, logout]);

  useEffect(() => {
    if (!isOkOutcome) {
      return undefined;
    }

    if (!sessionRequestRef.current) {
      beginCallbackSessionAttempt();
      sessionRequestRef.current = establishSession(login, logout);
    }

    const controller = new AbortController();
    sessionRequestRef.current.then((nextPhase) => {
      if (!controller.signal.aborted) {
        setSessionPhase(nextPhase);
      }
    });

    return () => controller.abort();
  }, [isOkOutcome, login, logout]);

  const hasSessionFailed = sessionPhase === SessionPhase.FAILED;
  const isUserUnavailable =
    sessionPhase === SessionPhase.AWAITING_USER && !hasAuthToken;

  if (isInvalidOutcome) {
    return <Redirect to={LOGIN_ROUTE} />;
  }

  if (isErrorOutcome) {
    return <CallbackFailureView failure={outcome.failure} />;
  }

  if (hasSessionFailed) {
    return (
      <CallbackFailureView failure={CallbackFailure.SESSION_UNAVAILABLE} />
    );
  }

  if (isUserUnavailable) {
    return <CallbackFailureView failure={CallbackFailure.USER_UNAVAILABLE} />;
  }

  return <LayoutSplashScreen />;
}

export { Callback as UnconnectedCallback };
export default connect(
  ({ auth }: RootState) => ({ hasAuthToken: Boolean(auth.authToken) }),
  { login: auth.actions.login, logout: auth.actions.logout },
)(Callback);
